
import FormInput from '@/components/release/form/input'
import FormSingle from '@/components/release/form/single'
import FormCheckbox from '@/components/release/form/checkbox'
import FormCascade from '@/components/release/form/cascade'
import Upload from '@/components/Upload/releaseUpload'
import { UPLOAD_COMMON_TYPE } from '@/enum/uploadType'

export default {
    name: 'DynamicFormField',
    components: {
        FormInput,
        FormSingle,
        FormCheckbox,
        FormCascade,
        Upload
    },
    props: {
        // 表单项配置对象
        item: {
            type: Object,
            required: true,
            default: () => ({})
        },
        // v-model 绑定的值
        value: {
            type: [String, Number, Array, Boolean],
            default: null
        },
        // 服务费列表
        feeDataList: {
            type: Array,
            default: null
        },
        // 表单值
        formValues: {
            type: Object,
            default: () => ({})
        },
        releaseData: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            UPLOAD_COMMON_TYPE,
            // 疑问说明对话框显示状态
            dialogQuestion: false
        }
    },
    computed: {
        fieldValue: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },

        // 计算配置对象，避免模板中重复调用
        config() {
            return this.getFieldConfig()
        },
        // 计算服务费显示数据
        feeShow() {
            if (!this.fieldValue || !this.feeDataList?.length || !['price', 'skuPrice'].includes(this.item.mappingField)) {
                return {
                    show: false,
                    list: [],
                    income: 0,
                    totalServiceFee: 0
                }
            }

            const price = parseFloat(this.fieldValue)
            if (price <= 0 || !/(^[1-9]\d*$)/.test(this.fieldValue)) {
                return {
                    show: false,
                    list: [],
                    income: 0,
                    totalServiceFee: 0
                }
            }

            // 计算服务费
            const feeResult = []
            this.feeDataList.forEach(item => {
                let fee = {
                    fee: 0,
                    name: item.feeTypeName
                }
                let targetFee = item.fees.find(feeItem => feeItem.priceMin <= price && feeItem.priceMax > price)
                if (!targetFee) return

                if (targetFee.feeWay === 'FIXED') {
                    // fee.name += '(固定)'
                    fee.fee = targetFee.fee.toFixed(2)
                } else {
                    // fee.name += `(费率${targetFee.fee}%)`
                    fee.fee = (price * (targetFee.fee / 100)).toFixed(2)
                }

                if (Number(fee.fee) === 0) return
                feeResult.push(fee)
            })

            const totalServiceFee = feeResult.reduce((total, item) => total + Number(item.fee), 0)
            /**
             * 格式化小数显示，最多四位小数，有几位显示几位，没有小数不显示小数
             * @param {number} num 要格式化的数字
             * @returns {string} 格式化后的字符串
            */
            function formatDecimal(num) {
                if (Number.isNaN(num) || !Number.isFinite(num))
                    return 0

                // 保留最多4位小数，然后移除末尾的0
                const fixed = Number(num).toFixed(4)
                const trimmed = Number.parseFloat(fixed) || 0.0001
                return trimmed
            }

            const unitPriceObj = {}
            const priceFields = this.item.priceFields
            const unitNum = this.formValues.unitNum
            if (priceFields && Object.keys(priceFields)?.length) {
                // 单价 数额/价格
                if ('unitNumPrice' in priceFields) {
                    // 数额/价格
                    const result = unitNum ? unitNum / price : 0
                    unitPriceObj.unitNumPrice = formatDecimal(result)
                }
                if ('unitPriceNum' in priceFields) {
                    // 价格/数额
                    const result = unitNum ? price / unitNum : 0
                    unitPriceObj.unitPriceNum = formatDecimal(result)
                }
                unitPriceObj.unit = priceFields.unit
            }

            return {
                show: !!feeResult.length,
                list: feeResult,
                income: (price - totalServiceFee).toFixed(2),
                totalServiceFee: totalServiceFee.toFixed(2),
                ...unitPriceObj
            }
        },
        showUnitPrice() {
            return (this.item.mappingField === 'price' || this.item.mappingField === 'skuPrice') && this.item?.priceFields && ('unitNumPrice' in this.item?.priceFields || 'unitPriceNum' in this.item?.priceFields)
        },
        rule() {
            const result = this.evaluateRules(this.item, this.formValues)
            result.rules = this.getFormItemRules(result.required)
            return result
        }
    },
    watch: {
        rule: {
            handler(newVal) {
                console.log(newVal, 'rule');
                if (newVal.display === false) {
                    this.fieldValue = null
                }
            },
            deep: true
        }
    },
    methods: {
        // 安全创建正则表达式规则
        createTextRuleValidation(textRule, textRuleDesc) {
            if (!textRule)
                return []
            const regex = this.createSafeRegex(textRule)
            return regex ? [{ pattern: regex, message: textRuleDesc, trigger: ['onBlur', 'onSubmit'] }] : []
        },
        // 获取字段配置（根据 mappingField 定制化）
        getFieldConfig() {
            const { mappingField, propertyName, fieldType, valueTip, valueSize, disabled, exampleImage, options } = this.item
            // 每种类型的默认配置
            const typeBind = {
                TEXT: {
                    inputType: 'text',
                    placeholder: valueTip || `请输入${propertyName}`,
                    maxLength: valueSize,
                    disabled: disabled,
                    showWordLimit: false,
                    clearable: true,
                    autosize: false,
                },
                SECTION: {
                    inputType: 'number',
                    placeholder: valueTip,
                    controls: false,
                    maxLength: valueSize,
                },
                IMAGE: {
                    uploadType: UPLOAD_COMMON_TYPE['goodsCover']?.value,
                    exampleImage: exampleImage,
                    valueTip,
                    multiple: true,
                    limit: valueSize || 1,
                    name: mappingField
                },
                SINGLE: {
                    options,
                    valueTip
                },
                CHECKBOX: {
                    options,
                    multiple: true,
                    valueTip,
                    valueType: this.releaseData.goodsCatalogueId === 6 ? 'value' : 'key' // 通用商品传key 其他商品传id
                },
                SINGLE_CASCADE: {
                    options,
                    valueTip
                }
            }
            // 字段特殊配置映射
            const fieldConfigs = {
                // 卖家留言
                sellMessage: {
                    inputType: 'textarea',
                    autosize: { minHeight: 48 },
                    maxLength: valueSize || 50,
                    showWordLimit: true
                },
                description: {
                    inputType: 'textarea',
                    showWordLimit: true,
                    autosize: { minRows: 6 },
                    maxLength: valueSize || 1000
                },
                gameSecret: { inputType: 'password' },
                linkPhone: { inputType: 'tel', maxLength: 11 },
                linkQq: { inputType: 'digit' },  // 只允许整数
                price: { inputType: 'digit', maxLength: 10 },  // 只允许整数
                skuPrice: { inputType: 'digit', maxLength: 10 },
                stock: { inputType: 'digit' },  // 只允许整数
                skuStock: { inputType: 'digit' },  // 只允许整数
                unitNum: { inputType: 'digit' },  // 只允许整数

                // IMAGE 字段配置
                [this.releaseData.gameId === 6 ? 'goodsImg' : 'goodsImage']: { upLoadType: UPLOAD_COMMON_TYPE['goodsCover']?.value },
                detailsImages: { upLoadType: UPLOAD_COMMON_TYPE['goodsDetail']?.value, limit: 100 }
            }

            // 获取特殊配置
            const typeConfig = typeBind[fieldType] || {}
            const specialConfig = fieldConfigs[mappingField] || {}
            return { ...typeConfig, ...specialConfig }
        },
        // 获取 el-form-item 的 class
        getFormItemClass() {
            switch (this.item.fieldType) {
                case "SINGLE_CASCADE":
                    return 'single-cascade-form-item'
                case "TEXT":
                case "SECTION":
                    return 'input-form-item'
                case "SINGLE":
                case "CHECKBOX":
                    return 'choose-form-item'
                case "IMAGE":
                    return 'image-form-item'
                default:
                    return ''
            }
        },
        // 获取 el-form-item 的验证规则
        getFormItemRules(required) {
            const { propertyName, textRule, textRuleDesc, fieldType, mappingField } = this.item

            const rules = []

            // 必填验证
            if (required) {
                const isSelectType = ['IMAGE', 'SINGLE', 'CHECKBOX', 'SINGLE_CASCADE'].includes(fieldType)
                const message = isSelectType ? '请选择' + propertyName : '请输入' + propertyName

                rules.push({
                    required: true,
                    message,
                    trigger: ['blur']
                })
            }

            // 格式验证（仅适用于输入字段）
            if (fieldType === 'TEXT') {
                const pattern = textRule ? new RegExp(textRule) : ''
                if (pattern) {
                    rules.push({
                        pattern,
                        message: textRuleDesc || `请输入正确的${propertyName}`,
                        trigger: ['blur', 'change']
                    })
                }
            }

            // SECTION 类型的范围校验
            if (fieldType === 'SECTION') {
                const { valueMin, valueMax } = this.item

                if (valueMin !== undefined && valueMin !== null) {
                    rules.push({
                        validator: (rule, value, callback) => {
                            if (value === '' || value === null || value === undefined) {
                                callback()
                                return
                            }
                            const numValue = Number(value)
                            if (numValue < valueMin) {
                                callback(new Error(`${propertyName}不能小于${valueMin}`))
                                return
                            }
                            callback()
                        },
                        trigger: ['blur', 'change']
                    })
                }

                if (valueMax !== undefined && valueMax !== null) {
                    rules.push({
                        validator: (rule, value, callback) => {
                            if (value === '' || value === null || value === undefined) {
                                callback()
                                return
                            }
                            const numValue = Number(value)
                            if (numValue > valueMax) {
                                callback(new Error(`${propertyName}不能大于${valueMax}`))
                                return
                            }
                            callback()
                        },
                        trigger: ['blur', 'change']
                    })
                }
            }
            // 价格的定制化校验
            if (mappingField === 'price' || mappingField === 'skuPrice') {
                rules.push({
                    validator: (rule, value, callback) => {
                        // 价格不为空的时候进行校验
                        if (![null, ''].includes(value)) {
                            const currPrice = Number(value)
                            const minPrice = Number(this.feeShow.totalServiceFee)
                            if (currPrice > 99999999) {
                                callback(new Error('高于最高可售卖价格99999999元, 请重新设置账号价格'))
                            }
                            if (currPrice < minPrice) {
                                callback(new Error(`低于最低可售卖价格${minPrice}元, 请重新设置账号价格`))
                            }
                            callback()
                        }
                    },
                    trigger: ['blur', 'change']
                })
            }

            return rules
        },
        // 评估规则条件，判断字段是否应该显示
        evaluateRules(item, formValues) {
            // 默认显示且按照原始required属性
            const defaultResult = {
                display: true,
                required: item.required
            }

            // 如果没有规则，使用默认值
            const rule = item.rule
            if (!rule || !rule.sucExecute || !rule.conditions || !rule.matchType)
                return defaultResult

            const conditions = rule.conditions || []

            // 如果没有条件，使用默认值
            if (!conditions.length)
                return defaultResult

            // 评估每个条件
            const conditionResults = conditions.map(condition => {
                const { conditionType, mappingField, value } = condition

                // 获取字段值
                const fieldValue = formValues[mappingField]

                // 处理特殊情况：字段值是否为空
                const isFieldValueEmpty = fieldValue === undefined || fieldValue === null || fieldValue === ''
                    || (Array.isArray(fieldValue) && fieldValue.length === 0)

                // 确保value是数组
                const valueArray = Array.isArray(value) ? value : [value]
                // 如果value数组为空，使用空字符串作为默认值
                const firstValue = valueArray.length > 0 ? valueArray[0] : ''

                // 将字段值转换为字符串进行比较（如果不是数组）
                const stringFieldValue = Array.isArray(fieldValue)
                    ? null // 如果是数组，不转换为字符串
                    : String(fieldValue === undefined || fieldValue === null ? '' : fieldValue)

                switch (conditionType) {
                    case 'CONTAIN': // 包含
                        if (Array.isArray(fieldValue)) {
                            // 如果字段值是数组，检查是否包含value数组的第一个项
                            return valueArray.every(item => fieldValue.includes(item))
                        }
                        // 否则将字段值转为字符串，与value数组的第一个项比较
                        return stringFieldValue.includes(String(firstValue))

                    case 'EQUALS': // 等于
                        if (Array.isArray(fieldValue)) {
                            // 如果字段值是数组，检查是否与value数组完全匹配
                            // 首先检查长度是否相同
                            if (valueArray.length !== fieldValue.length) {
                                return false
                            }

                            // 然后检查每个元素是否都能在另一个数组中找到匹配项
                            return fieldValue.every(item => {
                                return valueArray.includes(item)
                            })
                        }
                        // 否则直接比较值是否相等
                        return fieldValue === firstValue

                    case 'IS_NULL': // 为空
                        return isFieldValueEmpty

                    case 'NOT_CONTAIN': // 不包含
                        if (Array.isArray(fieldValue)) {
                            // 如果字段值是数组，检查是否不包含value数组的第一个项
                            return !valueArray.some(item => fieldValue.includes(item))
                        }
                        // 否则将字段值转为字符串，与value数组的第一个项比较
                        return !stringFieldValue.includes(String(firstValue))

                    case 'NOT_EQUALS': // 不等于
                        if (Array.isArray(fieldValue)) {
                            // 如果字段值是数组，检查是否没有元素等于value数组的第一个项
                            return !(fieldValue.some(item => valueArray.includes(item)) && valueArray.some(item => fieldValue.includes(item)))
                        }
                        // 否则直接比较值是否不相等
                        return fieldValue !== firstValue

                    case 'NOT_NULL': // 不为空
                        return !isFieldValueEmpty

                    default:
                        return false
                }
            })

            // 根据匹配类型判断最终结果
            const finalConditionResult = rule.matchType === 'ALL'
                ? conditionResults.every(result => result) // 所有条件都满足
                : conditionResults.some(result => result) // 满足其中一个条件

            // 根据成功执行类型设置显示和必填属性
            switch (rule.sucExecute) {
                case 'DISPLAY_NOT_REQUIRED': // 出现且非必填
                    return finalConditionResult ? { display: true, required: false } : { display: false, required: false }
                case 'DISPLAY_REQUIRED': // 出现且必填
                    return finalConditionResult ? { display: true, required: true } : { display: false, required: false }
                case 'NOT_DISPLAY': // 不出现
                    return finalConditionResult ? { display: false, required: false } : defaultResult
                default:
                    return defaultResult
            }
        },
        // 处理疑问说明点击事件
        handleQuestionClick() {
            this.dialogQuestion = true
        },
        handleChooseChange(val) {
            this.fieldValue = val
            if (![null, undefined, ''].includes(val)) {
                this.$nextTick(() => {
                    this.$refs.formItemRef?.clearValidate()
                })
            }
        }
    }
}
